import { apiService } from './index';
import { cleanObj } from '../shared/utils/objects';

// Inport types
import { ISelect, ParamEnum } from '../shared/types/shared';
import {
    RedirectParams,
    RedirectParamsHistorical,
} from '../shared/types/redirectParams';
import { ProcedureParamsHistorical } from '../shared/types/systemConfiguration/procedureParams';

import {
    Motive,
    MotiveHistorical,
} from '../shared/types/systemConfiguration/motives';
import { Param, ParamHistorical } from '../shared/types/param';
import { PqrTimeFull } from '../shared/types/paramPqrTimes';

export const asyncGetParams = async (
    classParam?: ParamEnum,
    parentCode?: string,
    parentId?: any,
    name?: string,
    extra?: string,
    active?: boolean,
    page?: number,
    pageSize?: number,
    valueSmart?: string
): Promise<ISelect[]> => {
    try {
        const { items }: any = await apiService.get(`/api/param`, {
            class: classParam || '',
            parentCode: parentCode || '',
            parentId: parentId || '',
            name: name || '',
            extra: extra || '',
            active: active,
            limit: pageSize || 0,
            page: page || 0,
            valueSmart: valueSmart || '',
        });

        return items || [];
    } catch (error) {
        return [];
    }
};

export const asyncGetParamsWithNestedParam = async (
    classParam?: ParamEnum,
    parentName?: any,
    nestedParentName?: any,
    name?: string,
    extra2?: string,
    active?: boolean,
    page?: number,
    pageSize?: number,
    maxNestedLevel?: number,
    nestedParentName2?: any,
    nestedParentName3?: any,
    nestedParentName4?: any
): Promise<ISelect[]> => {
    try {
        const { items }: any = await apiService.get(`/api/param/nested-param`, {
            class: classParam || '',
            parentName: parentName || '',
            nestedParentName: nestedParentName || '',
            name: name || '',
            extra2: extra2 || '',
            active: active,
            limit: pageSize || 0,
            page: page || 0,
            maxNestedLevel: maxNestedLevel || 2,
            nestedParentName2: nestedParentName2 || '',
            nestedParentName3: nestedParentName3 || '',
            nestedParentName4: nestedParentName4 || '',
        });

        return items || [];
    } catch (error) {
        return [];
    }
};

export const asyncGetParamsWithGroupParent = async (
    classParam?: ParamEnum,
    parent?: string[],
    name?: string,
    extra?: string,
    active?: boolean,
    page?: number,
    pageSize?: number
): Promise<ISelect[]> => {
    try {
        const { items }: any = await apiService.get(
            `/api/param/group-by-parent`,
            {
                class: classParam || '',
                parent: parent || [],
                name: name || '',
                extra: extra || '',
                active: active,
                limit: pageSize || 0,
                page: page || 0,
            }
        );

        return items || [];
    } catch (error) {
        return [];
    }
};

export const asyncGetLabelParams = async (
    classParam?: ParamEnum
): Promise<ISelect[]> => {
    try {
        if (!Boolean(classParam?.trim())) return [];

        const { items }: any = await apiService.get(
            `/api/param/labels/${classParam}`
        );

        return items || [];
    } catch (error) {
        return [];
    }
};

export const asyncGetParamByCode = async (
    code: string
): Promise<ISelect | undefined> => {
    try {
        const { items }: any = await apiService.get(
            `/api/param/by-code/${code}`
        );
        return items;
    } catch (error) {}
};

export const asyncGetParamById = async (
    id: string
): Promise<ISelect | undefined> => {
    try {
        const { items }: any = await apiService.get(`/api/param/${id}`);
        return items || [];
    } catch (error) {}
};

export const asyncDeleteParam = async (id: string | number) => {
    return await apiService.delete(`/api/param/${id}`);
};

export const asyncReactivateParam = async (id: string | number) => {
    return await apiService.patch(`/api/param/reactivate/${id}`);
};

export const asyncPostParam = async (
    formParam: RedirectParams
): Promise<RedirectParams> => {
    return await apiService.post('/api/param', formParam);
};

export const asyncPostMotiveParam = async (
    formParam: Motive
): Promise<Motive> => {
    return await apiService.post('/api/param/motive', formParam);
};

export const asyncPostSubmotive1Param = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/submotive1', formParam);
};

export const asyncPostSubmotive2Param = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/submotive2', formParam);
};

export const asyncPostSubmotive3Param = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/submotive3', formParam);
};

export const asyncPostSubmotive4Param = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/submotive4', formParam);
};

export const asyncPostPqrTypeParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/pqr-type', formParam);
};

export const asyncPostSubtypeDPParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/subtype-dp', formParam);
};

export const asyncPostPqrReceptionChannelParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/reception-channel', formParam);
};

export const asyncPostPqrReceivedThroughParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/received-through', formParam);
};

export const asyncPostRedirectAreaParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/redirect-area', formParam);
};

export const asyncPostFraudModeParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/fraud-mode', formParam);
};

export const asyncPostFraudTypeParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/fraud-type', formParam);
};

export const asyncPostPqrStatusParam = async (
    formParam: Param
): Promise<Param> => {
    return await apiService.post('/api/param/pqr-status', formParam);
};

export const asyncGetParam = async (id: string | number | undefined) => {
    return await apiService.get(`/api/param/${id}`);
};

export const asyncGetParamWithNestedParent = async (
    id: string | number | undefined,
    maxNestedLevel?: number
) => {
    return await apiService.get(
        `/api/param/nested-param/${id}/${maxNestedLevel || 2}`
    );
};

export const asyncGetMotive = async (
    name: string | undefined,
    active: boolean | undefined
) => {
    const encodedName = encodeURIComponent(name || '');
    return await apiService.get(`/api/param/motive/${encodedName}/${active}`);
};

export const asyncPatchtRedirectPersonParam = async (
    id: string,
    formParam: RedirectParams
): Promise<RedirectParams> => {
    return await apiService.patch(
        `/api/param/redirect-person/${id}`,
        formParam
    );
};

export const asyncPatchRedirectAreaParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/redirect-area/${id}`, formParam);
};

export const asyncPatchtProcedureParam = async (
    id: string,
    formParam: RedirectParams
): Promise<RedirectParams> => {
    return await apiService.patch(`/api/param/procedure/${id}`, formParam);
};

export const asyncPatchMotiveParam = async (
    name: string,
    active: boolean,
    formParam: Motive
): Promise<Motive> => {
    const encodedName = encodeURIComponent(name || '');
    return await apiService.patch(
        `/api/param/motive/${encodedName}/${active}`,
        formParam
    );
};

export const asyncPatchSubmotive1Param = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/submotive1/${id}`, formParam);
};

export const asyncPatchSubmotive2Param = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/submotive2/${id}`, formParam);
};

export const asyncPatchSubmotive3Param = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/submotive3/${id}`, formParam);
};

export const asyncPatchSubmotive4Param = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/submotive4/${id}`, formParam);
};

export const asyncPatchPqrTypeParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/pqr-type/${id}`, formParam);
};

export const asyncPatchPqrStatusParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/pqr-status/${id}`, formParam);
};

export const asyncPatchSubtypeDPParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/subtype-dp/${id}`, formParam);
};

export const asyncPatchPqrReceptionChannelParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(
        `/api/param/reception-channel/${id}`,
        formParam
    );
};

export const asyncPatchPqrReceivedThroughParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(
        `/api/param/received-through/${id}`,
        formParam
    );
};

export const asyncPatchFraudModeParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/fraud-mode/${id}`, formParam);
};

export const asyncPatchFraudTypeParam = async (
    id: string,
    formParam: Param
): Promise<Param> => {
    return await apiService.patch(`/api/param/fraud-type/${id}`, formParam);
};

export const asyncPatchtComplianceTerms = async (
    id: string,
    formParam: RedirectParams
): Promise<RedirectParams> => {
    return await apiService.patch(`/api/param/procedure/${id}`, formParam);
};

export const asyncGetLastCodeParam = async (classParam?: ParamEnum) => {
    return await apiService.get(`/api/param/last-code/${classParam}`);
};

export const asyncGetLastCodeSuffixParam = async (classParam?: ParamEnum) => {
    return await apiService.get(`/api/param/last-code-suffix/${classParam}`);
};

export const asyncGetParamPqrTimes = async (filters: {
    status?: string;
    receptionChannel?: string;
    type?: string;
    half?: string;
    subtypeDp?: string;
    procedure?: string;
}): Promise<PqrTimeFull | undefined> => {
    const filtersClean = cleanObj(filters);
    try {
        const { items }: any = await apiService.get(
            `/api/pqr-times/filters`,
            filtersClean
        );
        return items;
    } catch (error) {}
};

export const asyncGetPqrReceivedThroughWithAlertTemplate = async (
    id: string
): Promise<ISelect | undefined> => {
    try {
        const { items }: any = await apiService.get(
            `/api/param/received-through/${id}`
        );
        return items;
    } catch (error) {}
};

export const asyncGetParamsClassName = async (
    classParam?: ParamEnum,
    name?: string,
    extra?: string,
    parent?: any,
    page?: number,
    pageSize?: number,
    active?: boolean
): Promise<ISelect[]> => {
    try {
        const { items }: any = await apiService.get(`/api/param/class-name`, {
            class: classParam || '',
            name: name || '',
            extra: extra || '',
            parentId: parent || '',
            active: active,
            limit: pageSize || 0,
            page: page || 0,
        });

        return items || [];
    } catch (error) {
        return [];
    }
};

export const asyncGetMotiveHistorical = async (
    name: string,
    active: boolean
): Promise<MotiveHistorical[] | undefined> => {
    try {
        const encodedName = encodeURIComponent(name || '');
        const {
            items: data,
        }: {
            items: MotiveHistorical[];
        } = await apiService.get(
            `/api/param/motive/${encodedName}/${active}/historical`
        );
        return data;
    } catch (error) {}
};

export const asyncGetSubmotive1Historical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/submotive1/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetSubmotive2Historical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/submotive2/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetSubmotive3Historical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/submotive3/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetSubmotive4Historical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/submotive4/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetPqrTypeHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/pqr-type/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetRedirectParamHistorical = async (
    id: string
): Promise<RedirectParamsHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: RedirectParamsHistorical[];
        } = await apiService.get(`/api/param/${id}/historical`);

        return data || [];
    } catch (error) {}
};

export const asyncGetProcedureParamHistorical = async (
    id: string
): Promise<ProcedureParamsHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ProcedureParamsHistorical[];
        } = await apiService.get(`/api/param/procedure/${id}/historical`);

        return data || [];
    } catch (error) {}
};

export const asyncGetSubtypeDPHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/subtype-dp/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetPqrReceptionChannelHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(
            `/api/param/reception-channel/${id}/historical`
        );
        return data;
    } catch (error) {}
};

export const asyncGetPqrReceivedThroughHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(
            `/api/param/received-through/${id}/historical`
        );
        return data;
    } catch (error) {}
};

export const asyncGetRedirectAreaHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/redirect-area/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetFraudModeHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/fraud-mode/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetFraudTypeHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/fraud-type/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetPqrStatusHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/pqr-status/${id}/historical`);
        return data;
    } catch (error) {}
};

export const asyncGetComplianceTermsHistorical = async (
    id: string
): Promise<ParamHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: ParamHistorical[];
        } = await apiService.get(`/api/param/procedure/${id}/historical`);

        return data || [];
    } catch (error) {}
};
